import { money, statusLabel, STATUS_STYLES } from "@/lib/format";

export function PageHeader({ title, description, action, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy font-display">{title}</h1>
        {description && <p className="mt-1 text-slate-500">{description}</p>}
      </div>
      <div className="flex items-center gap-2">{action}{children}</div>
    </div>
  );
}

export function StatCard({ label, value, sub, tone = "default", icon: Icon, onClick }) {
  const tones = {
    default: "text-navy", green: "text-emerald-600", red: "text-red-600",
    blue: "text-blue-accent", amber: "text-amber-600",
  };
  return (
    <div onClick={onClick} className={`amden-card p-5 ${onClick ? "cursor-pointer hover:shadow-md hover:border-blue-200 transition-shadow" : ""}`} data-testid={`stat-${String(label).toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">{label}</p>
        {Icon && <Icon size={18} className="text-slate-300" />}
      </div>
      <p className={`mt-2 text-2xl font-semibold font-mono ${tones[tone]}`}>{value}</p>
      {sub && <p className="mt-1 text-sm text-slate-400">{sub}</p>}
    </div>
  );
}

export function Money({ value, symbol, className = "" }) {
  return <span className={`font-mono tabular ${className}`}>{money(value, symbol)}</span>;
}

export function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return <span data-testid={`status-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{statusLabel(status)}</span>;
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="amden-card p-12 text-center">
      {Icon && <div className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-slate-100 text-slate-400"><Icon size={26} /></div>}
      <h3 className="mt-4 font-semibold text-navy text-lg">{title}</h3>
      {description && <p className="mt-1 text-slate-500 max-w-sm mx-auto">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Toolbar({ children }) {
  return <div className="flex flex-wrap items-center gap-2 mb-4">{children}</div>;
}

export function SearchInput({ value, onChange, placeholder = "Search…", testId }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder} data-testid={testId}
      className="w-full sm:w-64 rounded-lg border border-slate-300 px-3.5 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
  );
}

export function Btn({ variant = "primary", className = "", as: As = "button", ...props }) {
  const styles = {
    primary: "bg-navy hover:bg-slate-800 text-white shadow-sm",
    accent: "bg-blue-600 hover:bg-blue-700 text-white shadow-sm",
    secondary: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm",
    ghost: "hover:bg-slate-100 text-slate-600",
    danger: "bg-red-600 hover:bg-red-700 text-white shadow-sm",
  };
  return <As className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${styles[variant]} ${className}`} {...props} />;
}

export function Spinner() {
  return <div className="flex justify-center py-16"><div className="h-8 w-8 rounded-full border-4 border-slate-200 border-t-blue-600 animate-spin" /></div>;
}
